import { Alert, Button, Flex, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { Dropzone, MIME_TYPES, MS_WORD_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconFileText, IconUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useAutoBrandExtract } from 'src/secret-menu/auto-brand/hooks/useAutoBrand';
import ExtractionLoadingPanel from 'src/secret-menu/auto-brand/steps/ExtractionLoadingPanel';
import type { AutoBrandData, AutoBrandSourceKind } from 'src/secret-menu/auto-brand/types';
import { DEFAULT_CONFIG } from 'src/secret-menu/export-assets/types';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

function detectKind(file: File): AutoBrandSourceKind | null {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.docx')) return 'docx';
  return null;
}

export default function UploadDocumentStep(props: StepProps<AutoBrandData>) {
  const { data, setData, nextStep, previousStep, canGoBack } = props;
  const companyId = data.company?.id ?? '';
  const { extract, isPending } = useAutoBrandExtract(companyId);
  const [localFile, setLocalFile] = useState<File | null>(data.sourceFile?.file ?? null);

  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const kind = detectKind(file);
    if (!kind) {
      notifications.show({
        title: 'Unsupported file',
        message: 'Please upload a PDF or DOCX document.',
        color: 'red',
      });
      return;
    }
    setLocalFile(file);
    setData(prev => ({ ...prev, sourceFile: { file, kind } }));
  };

  const handleSkip = () => {
    setData(prev => ({
      ...prev,
      sourceFile: null,
      documentType: null,
      config: prev.existingBranding?.config ?? prev.config ?? DEFAULT_CONFIG,
    }));
    nextStep();
  };

  const handleExtract = async () => {
    if (!localFile) return;
    const kind = detectKind(localFile);
    if (!kind) return;
    try {
      const result = await extract({ file: localFile, kind });
      setData(prev => ({
        ...prev,
        sourceFile: { file: localFile, kind },
        documentType: result.documentType,
        config: result.config,
      }));
      nextStep();
    } catch (err) {
      notifications.show({
        title: 'Extraction failed',
        message: err instanceof Error ? err.message : 'The branding extractor failed.',
        color: 'red',
      });
    }
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Upload branding source (optional)"
      description="Drop a branded example document or brand guidelines pack. We'll analyse it with a vision model and pre-fill every branding field we can."
      continueText={localFile ? 'Extract branding' : 'Skip and configure manually'}
      canGoForward={!isPending}
      canGoBack={canGoBack}
      previousStep={() => previousStep()}
      nextStep={() => (localFile ? handleExtract() : handleSkip())}
      isSubmitting={isPending}
      showClientName={false}
      hideProgress={false}
      renderProgress={isPending ? () => <ExtractionLoadingPanel /> : undefined}
      allowVerticalOverflow
    >
      <Stack gap="lg">
        <Alert color="blue" variant="light" title="How extraction works">
          We accept PDF and DOCX files up to 50MB. PDFs are analysed page-by-page with a vision
          model; DOCX styles are read directly and augmented with visuals from embedded images.
        </Alert>

        <Dropzone
          onDrop={handleDrop}
          onReject={() =>
            notifications.show({
              title: 'Rejected',
              message: 'File must be a PDF or DOCX under 50MB.',
              color: 'red',
            })
          }
          maxSize={MAX_FILE_SIZE}
          maxFiles={1}
          accept={[...PDF_MIME_TYPE, ...MS_WORD_MIME_TYPE, MIME_TYPES.docx]}
          style={{
            border: '1px dashed var(--mantine-color-gray-4)',
            borderRadius: 12,
            padding: 40,
          }}
        >
          <Flex direction="column" align="center" gap="md">
            <Dropzone.Accept>
              <ThemeIcon variant="light" color="blue" size={56} radius="xl">
                <IconUpload size={28} />
              </ThemeIcon>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <ThemeIcon variant="light" color="red" size={56} radius="xl">
                <IconX size={28} />
              </ThemeIcon>
            </Dropzone.Reject>
            <Dropzone.Idle>
              <ThemeIcon variant="light" color="gray" size={56} radius="xl">
                <IconFileText size={28} />
              </ThemeIcon>
            </Dropzone.Idle>
            <Text fw={FONT_WEIGHTS.semibold} size="textLg">
              Drop a branding document here
            </Text>
            <Text c="gray.6" size="textSm">
              or click to browse — PDF or DOCX
            </Text>
          </Flex>
        </Dropzone>

        {localFile && (
          <Group justify="space-between" p="md" bg="gray.0" style={{ borderRadius: 8 }}>
            <Flex gap="sm" align="center">
              <IconFileText size={20} />
              <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                {localFile.name}
              </Text>
              <Text size="textSm" c="gray.6">
                {(localFile.size / 1024 / 1024).toFixed(1)} MB
              </Text>
            </Flex>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => {
                setLocalFile(null);
                setData(prev => ({ ...prev, sourceFile: null }));
              }}
            >
              Remove
            </Button>
          </Group>
        )}
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
